import React, { useState, useEffect, useContext } from 'react';
import Starter from "../../../../Assets/Images/Starter.png";
import home from "../../../../Assets/Images/selfhome.png";
import StarterChart from '../../../Charts/StarterChart';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from '../../../../Context/GroupReportContext';
import { initialData } from '../groupReport-pdf/QuadrantInFocus';

const styles = {
  pageWrapper: {
    backgroundColor: "rgb(255, 255, 255)",
    margin: "0 auto",
  },
  contents: {
    padding: "16px 0px",
  },
  containers: {
    display: "flex",
    flexDirection: "row",
    gap:"16px",
    marginBottom : "5px"
  },

  img: {
    height:"108px",
    width:"108px"
  },
  heading: {
    fontFamily: "Helvetica",
    fontWeight: 700,
    fontStyle: "Bold",
    fontSize: "18px",
    lineHeight: "24px",
    letterSpacing: "0px",
    textTransform: "uppercase",
    color: "#00577D",
    padding: "0px 0px 16px",
    margin: "0px"
  },
  paragraph: {
    fontFamily: "Helvetica",
    fontWeight: 400,
    fontStyle: "Regular",
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "0px",
    color: "#000000",
    marginTop: 0,
    marginBottom: "12px",
  },
  paragraphMain: {
    fontFamily: "Helvetica",
    fontWeight: 700,
    fontStyle: "Regular",
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "0px",
    color: "#000000",
    marginTop: 0,
    marginBottom: "12px",
  },
  label: {
    fontFamily: "Helvetica",
    fontWeight: 700,
    fontStyle: "Bold",
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "0px",
    color: "#000000",
  },
  boldParagraph: {
    fontFamily: "Helvetica",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "0px",
    color: "#000000",
    marginTop: 0,
    marginBottom: "16px",
  },
};

const smartRoundTo100 = (percentages) => {
  const total = percentages.reduce((a, b) => a + b, 0);
  const scaled = percentages.map(p => Math.round((p / total) * 100 * 100) / 100);
  const floored = scaled.map(p => Math.floor(p * 100) / 100);
  let remainder = 100 - floored.reduce((a, b) => a + b, 0);
  const deltas = scaled.map((p, i) => ({
    index: i,
    diff: p - floored[i],
    original: p
  }));

  deltas.sort((a, b) => (b.diff !== a.diff) ? (b.diff - a.diff) : (b.original - a.original));
  for (let i = 0; i < Math.round(remainder * 100); i++) {
    const index = deltas[i % deltas.length].index;
    floored[index] = Math.round((floored[index] + 0.01) * 100) / 100;
  }

  return floored;
};

export default function StarterQuardrant({ apiData, pageNumber, reportType }) {
  const [highestStarter, setHighestStarter] = useState('');
  const [snapshotResult, setSnapshotResults] = useState([]);
  const [variablsMapArray, setVariableMapArray] = useState();
  const [starterLabel, setStarterLabel] = useState([]);
  const [highestScoredVariable, setHighestScoredVariable] = useState('');
  const groupReportCtx = useContext(GroupReportContext);
  const [dynamicData, setDynamicData] = useState([...initialData]);
  const [top3, setTop3] = useState();
  const [allResultsState, setAllResultsState] = useState([]);

  const displayData = dynamicData?.filter((el) => el.quadrant === highestStarter?.toUpperCase());
  const allVariableSets = [apiData?.variables];

  const quadrantScores = {
    "HEART": 1,
    "SOUL": 2,
    "MIND": 3,
    "STRENGTH": 4
  };

  const variableMap = apiData?.variables?.reduce((acc, el) => {
    acc[el.variable] = el.score;
    return acc;
  }, {});

  const getMostScoredLabel = () => {
    if (highestStarter?.includes("heart")) return "HEART";
    if (highestStarter?.includes("soul")) return "SOUL";
    if (highestStarter?.includes("mind")) return "MIND";
    if (highestStarter?.includes("str")) return "STRENGTH";
    return "";
  };

  // Update allResultsState when highestStarter or context changes
  useEffect(() => {
    if (!highestStarter || !groupReportCtx) return;

    const quadrantKey = quadrantScores[highestStarter.toUpperCase()];
    const tempResults = [];

    displayData?.forEach((el, index) => {
      const res = groupReportCtx?.calculateQuestionScore(
        quadrantKey,
        allVariableSets,
        index + 1,
        "in_focus"
      );

      if (res?.averageScore !== null && quadrantKey === quadrantScores[el.quadrant]) {
        tempResults.push({
          ...res,
          question: el.question
        });
      }
    });

    setAllResultsState(tempResults);
  }, [highestStarter, groupReportCtx, apiData]);

  // Helper to get top 3 questions for the highest quadrant
  const getTop3Questions = () => {
    const sortedAllResults = [...allResultsState]
      ?.sort((a, b) => b.averageScore - a.averageScore)
      ?.filter((el) => el.averageScore >= 0);

    return sortedAllResults?.slice(0, 3);
  };

  useEffect(() => {
    if (variableMap) {
      setVariableMapArray(variableMap);
    }
  }, [apiData]);

  useEffect(() => {
    const avgPercentagesSelf = groupReportCtx?.averagePercentagesSelf?.map((el) => el?.avg);
    const avgPercentagesSelfLabel = ['mind', 'soul', 'strength', 'heart'];
    const maxAvgObj = groupReportCtx?.averagePercentagesSelf?.reduce((max, current) => {
      return current.avg > max.avg ? current : max;
    });

    setHighestStarter(maxAvgObj?.name?.toLowerCase());
    setSnapshotResults(avgPercentagesSelf);
    setStarterLabel(avgPercentagesSelfLabel);
    setHighestScoredVariable(maxAvgObj?.name?.toLowerCase());
  }, [apiData, variablsMapArray, groupReportCtx]);

  useEffect(() => {
    const top3 = getTop3Questions();
    setTop3(top3);
  }, [allResultsState]);

  return (
    <div>
      <HeaderPreview apiData={apiData} />
      <div style={styles.contents}>
        <h2 style={styles.heading}>
          STARTER QUADRANT
        </h2>
        <p style={styles.paragraphMain}>We all have a natural home base. It’s the quadrant we move from most often—the place that feels most familiar when we lead, relate, and make decisions. </p>
        <div style={styles.containers}>
          <img src={home} style={styles.img} />
         <div>
           <p style={styles.paragraph}>Your Starter Quadrant (Heart, Soul, Mind, or Strength™) reflects your most instinctive way of being. It’s shaped by your wiring, experiences, and the roles you’ve played. This insight helps you understand how you naturally show up at work, in relationships, and under pressure.</p>
          <p style={styles.paragraph}>For individuals, it brings clarity to how you lead and grow. For teams and companies, it builds stronger collaboration, better communication, and a leadership culture rooted in authenticity. </p>
         </div>
        </div>
        <p style={styles.paragraph}>All four quadrants are part of who you are. But knowing your starting point helps you grow with purpose and lead with greater impact.        </p>

        <p style={styles.boldParagraph}>
          <span style={styles.label}>HEART:</span> This quadrant describes the emotive center of our being—that part of us that feels, values, wills, and connects with others. We discover who we are, what we want, and who we want to relate to from this inner space. From this dimension, we build social constructs, find our tribe, and build a life with and for others. It answers the core question of <i>Who</i>. Collaborative Leadership is the style associated with Heart.
        </p>
        <p style={styles.boldParagraph}>
          <span style={styles.label}>SOUL:</span> This is the meaning center of our being—the part of us that seeks to make sense of our lives, our stories, and our purpose in the world. From this space, we dream, imagine, and create. We cast a vision for our lives, families, and business enterprises and then inspire ourselves and others toward that desired future. This quadrant answers the core question of <i>Why</i>. Visionary Leadership is the style associated with Soul.
        </p>
        <p style={styles.boldParagraph}>
          <span style={styles.label}>MIND:</span> This dimension describes the strategic center of our being—that part of us that organizes and structures our inner and outer worlds into intelligible systems. From this space, we seek to understand reality and sort it into logical connections that make sense and provide coherence and safety for ourselves and others. From the mind, we build and attach ourselves to theories and truths that give a container to hold our way of being in the world. This quadrant answers the core question of <i>What</i>. Strategic Leadership is the style associated with Mind.
        </p>
        <p style={styles.boldParagraph}>
          <span style={styles.label}>STRENGTH:</span> This quadrant describes the sensory center of our being—that part of us that allows us to absorb the tactile data around us and convert that information into action. We experience and engage the physical world from this space, gathering resources and adopting behaviors for accomplishing goals. This quadrant is anchored in our bodies and, as such, is distinct from the other three internal dimensions of self. It answers the core question of <i>How</i>. Performance Leadership is the style associated with Strength.
        </p>

        {/* <h3 style={{ fontSize: '24px', marginTop: 0, marginBottom: "14px", fontWeight: 700, fontFamily: "Lato", lineHeight: "48px", color: '#426B88' }} >STARTER QUADRANT OVERVIEW CHART</h3> */}
        {/* <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', width: '100%' }}>
          <div style={{ transform: 'translateX(-50px)' }}>
            <StarterChart
              originalData={snapshotResult}
              labels={starterLabel}
              ch='mind'
            />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <h4 style={{ fontSize: '19px', fontWeight: '700', marginTop: '26px', fontFamily: 'Lato', color: '#00577D', marginBottom: 0 }}>
            STRONGEST CONFIRMATIONS AS A {getMostScoredLabel()}-STARTER
          </h4>
          <ol style={{ marginLeft: '10px', paddingLeft: '10px', marginTop: 0 }}>
            {top3?.length === 0 ? (
              <li style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, fontFamily: "Lato", color: '#00577D' }}>No data available.</li>
            ) : (
              top3?.map((q, idx) => (
                <li key={q.id ?? idx} style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, fontFamily: "Lato", color: '#00577D' }}>{q.question}</li>
              ))
            )}
          </ol>
        </div> */}
      </div>
      <FooterPreview reportType={reportType} pageNumber={pageNumber} />
    </div>
  );
}
